<script setup lang="ts">
import type { Test, TestResult } from "@/components/TestHarness/useTestHarnessRunner"
import { KButton } from "@kaleidoscope-solutions/ks-design-system"
import { computed, defineAsyncComponent, nextTick, onMounted, ref, watch } from "vue"
import CopyIcon from "@/components/FormulaPlayground/CopyIcon.svg?component"
import Loading from "@/components/Loading.vue"
import DeleteIcon from "@/components/TestHarness/DeleteIcon.svg?component"
import RunIcon from "@/components/TestHarness/RunIcon.svg?component"
import SyncIcon from "@/components/TestHarness/SyncIcon.svg?component"
import { useTestHarnessStore } from "@/stores/testHarness"
import { useSnackbar } from "@/utils/useSnackbar"

interface Props {
  test: Test
  display: TestResult
  deleteConfirm?: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (event: "run"): void
  (event: "delete"): void
  (event: "update:test", test: Test): void
}>()

const { addSnackbar } = useSnackbar()

const FormulaEditor = defineAsyncComponent(() => import("@/components/FormulaPlayground/FormulaEditor.vue"))

const testHarnessStore = useTestHarnessStore()

const localTest = ref<Test>({ ...props.test })

const expanded = ref(false)

function toggleExpanded() {
  expanded.value = !expanded.value
}

onMounted(() => {
  if (!props.test.name) {
    expanded.value = true
  }
})

watch(
  localTest,
  (val) => {
    testHarnessStore.updateTest(props.test.id, { ...val })
  },
  { deep: true }
)

function resultColor(result: TestResult) {
  if (result.error) {
    return "border-yellow-700"
  }
  if (result.pass === true) {
    return "border-green-400"
  }
  if (result.pass === false) {
    return "border-red-400"
  }
  return "border-gray-400"
}

function syncExpected() {
  localTest.value.expected = props.display.actual ?? ""
  nextTick (() => {
    emit("run")
  })
}

interface FieldConfig<TModel extends Record<string, any> = Test> {
  label: string
  model: keyof TModel
  resizable: boolean
  height: number
  color: string
}

const testFields = computed<FieldConfig<Test>[]>(() => [
  { label: "Name", model: "name", resizable: false, height: 65, color: "text-gray-700" },
  { label: "Notes", model: "note", resizable: true, height: 35, color: "text-gray-700" },
  // { label: "Context Id", model: "contextId", resizable: false, height: 35, color: "text-gray-700" }, // Hide for now
  { label: "Formula", model: "formula", resizable: true, height: 120, color: "text-gray-700" },
  { label: "Expected", model: "expected", resizable: true, height: 100, color: "text-gray-700" },
])

const readonlyFields = computed<FieldConfig<TestResult>[]>(() => [
  ...(props.display?.error
    ? [{
        label: "Error",
        model: "error" as keyof TestResult,
        resizable: true,
        height: 100,
        color: "border-yellow-400"
      }]
    : [{
        label: "Actual",
        model: "actual" as keyof TestResult,
        resizable: true,
        height: 100,
        color: resultColor(props.display)
      }])
])

function encodeForExcel(data: string) {
  // Replace tabs, newlines, and quotes for Excel compatibility
  return data
    .replace(/,\s\s/g, "\t")
}

function copyFieldValue(field: FieldConfig<Test> | FieldConfig<TestResult>) {
  let value = (localTest.value as any)[field.model] ?? (props.display as any)[field.model] ?? ""
  if (value) {
    if (field.label === "Expected" || field.label === "Actual") {
      value = value ? encodeForExcel(value) : ""
    }

    try {
      navigator.clipboard.writeText(value)
    } catch (error) {
      console.error(error)
      addSnackbar({
        type: "error",
        message: `Failed to copy ${field} to clipboard`,
      })
      return
    }

    addSnackbar({
      type: "success",
      message: `${field.label} copied to clipboard`,
    })
  }
}
</script>

<template>
  <div
    class="flex flex-col overflow-y-auto rounded-lg border border-gray-100 bg-white shadow-sm"
    :class="resultColor(display)"
  >
    <div class="flex flex-1 flex-col px-4 py-3">
      <!-- Collapsed header (default) -->
      <template v-if="!expanded">
        <div
          class="flex min-w-0 cursor-pointer items-start justify-between gap-2"
          role="button"
          tabindex="0"
          @click="toggleExpanded"
        >
          <div class="min-w-0 flex-1">
            <div class="font-semibold text-gray-900">
              {{ localTest.name || 'Unnamed test' }}
            </div>
            <div class="mt-1 truncate text-sm text-gray-600">
              <span class="font-medium">Formula:</span>
              <span class="truncate">{{ localTest.formula }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Expanded card with fields -->
      <template v-else>
        <div
          class="flex min-w-0 items-center justify-between gap-2 select-none"
          @click="toggleExpanded"
        >
          <div class="flex min-w-0 items-center gap-2">
            <span class="font-semibold whitespace-normal">{{ localTest.name || 'Unnamed test' }}</span>
          </div>
          <div class="flex items-center gap-2">
            <KButton
              v-if="display.actual !== undefined && !display.pass"
              variety="secondary"
              color="teal"
              class="px-2 py-1 shadow-none"
              title="Sync expected with actual"
              @click.stop="syncExpected"
            >
              <SyncIcon class="size-5" />
            </KButton>
            <KButton
              variety="primary"
              color="violet"
              class="px-2 py-1 shadow-none"
              title="Run"
              :disabled="display.running"
              @click.stop="$emit('run')"
            >
              <RunIcon
                v-if="!display.running"
                class="size-5"
              />
              <Loading
                v-else
                class="m-auto size-5"
              />
            </KButton>
            <KButton
              variety="secondary"
              color="red"
              class="px-2 py-1 shadow-none"
              title="Delete test"
              @click.stop="$emit('delete')"
            >
              <template v-if="deleteConfirm === test.id">
                Confirm?
              </template>
              <template v-else>
                <DeleteIcon class="size-5" />
              </template>
            </KButton>
          </div>
        </div>

        <div class="flex flex-1 flex-col gap-2 overflow-y-auto px-4 pt-2 pb-4">
          <div
            v-for="field in testFields"
            :key="field.model"
            class="flex flex-col"
          >
            <label
              class="flex flex-col font-medium text-gray-700"
              :class="{ 'flex-1': field.model === 'formula' }"
            >
              <span>
                <button
                  :title="`Copy ${field.label}`"
                  class="group flex items-center gap-1 text-sm font-normal hover:text-violet-700"
                  @click="copyFieldValue(field)"
                >
                  {{ field.label }}
                  <CopyIcon class="size-4 text-violet-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                </button>
              </span>
            </label>
            <FormulaEditor
              v-if="field.model === 'formula'"
              class="ph-no-capture flex flex-1"
              :model-value="localTest[field.model] ?? ''"
              placeholder="Formula"
              :name="field.model"
              @update:model-value="(val: string) => (localTest[field.model] = val)"
              @run-formula="$emit('run')"
            />
            <textarea
              v-else
              v-model.lazy="localTest[field.model]"
              :name="field.model"
              class="allow-undo rounded-sm border border-gray-300 bg-white px-2 py-1 tracking-wider text-gray-900"
              :class="[field.resizable ? 'resize-y' : 'resize-none', field.color]"
              :style="{ height: field.height ? `${field.height}px` : undefined }"
            />
          </div>

          <div
            v-for="field in readonlyFields"
            :key="field.model"
            class="flex flex-col"
          >
            <label class="flex flex-col font-medium text-gray-700">
              <span>
                <button
                  :title="`Copy ${field.label}`"
                  class="group flex items-center gap-1 text-sm font-normal hover:text-violet-700"
                  @click="copyFieldValue(field)"
                >
                  {{ field.label }}
                  <CopyIcon class="size-4 text-violet-600 opacity-0 transition-opacity duration-150 group-hover:opacity-100" />
                </button>
              </span>
            </label>
            <textarea
              :name="field.model"
              :value="display[field.model] as string ?? ''"
              readonly
              class="rounded-sm border border-gray-300 bg-gray-100 px-2 py-1 text-gray-800"
              :class="[field.resizable ? 'resize-y' : 'resize-none', field.color, field.model === 'error' ? 'font-mono' : '']"
              :style="{ height: field.height ? `${field.height}px` : undefined }"
            />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
